import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchProperty } from "../lib/api";
import { Heart, Bed, Bath, Maximize, MapPin, Building, Layers, Flame, Car, Trees, ArrowLeft, MessageCircle, Phone, Check, ExternalLink } from "lucide-react";
import { formatTRY, PROPERTY_TYPE_LABEL, TRANSACTION_LABEL, waLink, telLink, COMPANY } from "../lib/config";
import { isFavorite, toggleFavorite } from "../lib/favorites";

const Fact = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-700" /></div>
    <div>
      <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold">{label}</div>
      <div className="text-sm text-slate-900 font-medium">{value || "—"}</div>
    </div>
  </div>
);

export default function PropertyDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [active, setActive] = useState(0);
  const [fav, setFav] = useState(false);

  useEffect(() => {
    fetchProperty(slug).then((d) => { setP(d); setFav(isFavorite(d.id)); }).catch(() => setP(null));
  }, [slug]);

  if (!p) {
    return (
      <div className="pt-32 pb-24 max-w-3xl mx-auto px-4 text-center">
        <div className="font-display text-3xl text-slate-900 mb-4">İlan yükleniyor…</div>
      </div>
    );
  }

  const images = p.images?.length ? p.images : ["https://images.unsplash.com/photo-1721815693498-cc28507c0ba2?w=1200"];
  const message = `Merhaba, ${p.title} ilanınız hakkında bilgi almak istiyorum.`;
  // Prefer the listing agent's phone; fall back to the company's numbers
  const agentPhone = p.agent_phone || COMPANY.phone;
  const agentWaRaw = p.agent_whatsapp_raw || COMPANY.whatsappRaw;
  const agentTel = `tel:+${(p.agent_whatsapp_raw || "").replace(/\D/g, "") || COMPANY.phoneRaw.replace(/\D/g, "")}`;
  const agentWa = `https://wa.me/${agentWaRaw}?text=${encodeURIComponent(message)}`;

  return (
    <div data-testid="property-detail" className="pt-28 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/ilanlar" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#C0562F] mb-6" data-testid="back-to-listings"><ArrowLeft className="w-4 h-4" /> İlanlara dön</Link>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* GALLERY */}
          <div className="lg:col-span-7">
            <div className="relative rounded-2xl overflow-hidden bg-slate-100 aspect-[16/10]">
              <img src={images[active]?.startsWith("http") ? images[active] : `${process.env.REACT_APP_BACKEND_URL}${images[active]}`} alt={p.title} className="w-full h-full object-cover" />
              <div className="absolute top-4 left-4 flex gap-2">
                <span className={`px-3 py-1 rounded-full text-[11px] font-bold tracking-wider uppercase ${p.transaction_type === "satilik" ? "bg-[#C0562F] text-white" : "bg-slate-900 text-white"}`}>{TRANSACTION_LABEL[p.transaction_type]}</span>
                {p.featured && <span className="px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white text-slate-900">Öne Çıkan</span>}
              </div>
              <button onClick={() => setFav(toggleFavorite(p.id))} data-testid="detail-fav-toggle" className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/95 backdrop-blur shadow flex items-center justify-center"><Heart className={`w-5 h-5 ${fav ? "fill-[#C0562F] text-[#C0562F]" : "text-slate-700"}`} /></button>
            </div>
            {images.length > 1 && (
              <div className="thumb-strip mt-4 flex gap-3 overflow-x-auto pb-2">
                {images.map((src, i) => (
                  <button key={i} onClick={() => setActive(i)} data-testid={`thumb-${i}`} className={`shrink-0 w-24 h-20 rounded-lg overflow-hidden border-2 transition-colors ${active === i ? "border-[#C0562F]" : "border-transparent"}`}>
                    <img src={src?.startsWith("http") ? src : `${process.env.REACT_APP_BACKEND_URL}${src}`} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* SUMMARY */}
          <div className="lg:col-span-5">
            <div className="sticky top-24">
              <div className="label-caps mb-2">{PROPERTY_TYPE_LABEL[p.property_type]}</div>
              <h1 className="font-display text-3xl sm:text-4xl text-slate-900 leading-tight mb-3">{p.title}</h1>
              <div className="flex items-center gap-2 text-slate-500 mb-6"><MapPin className="w-4 h-4" /><span>{p.neighborhood ? `${p.neighborhood}, ` : ""}{p.district}, {p.city}</span></div>

              <div className="p-6 rounded-2xl bg-slate-900 text-white mb-6">
                <div className="text-[11px] tracking-widest uppercase text-slate-400 font-semibold mb-1">Fiyat</div>
                <div className="font-display text-4xl">{formatTRY(p.price)}</div>
              </div>

              <div className="flex flex-col gap-3">
                <a href={agentWa} target="_blank" rel="noopener noreferrer" data-testid="detail-whatsapp-btn" className="w-full px-6 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 transition-colors text-white font-semibold flex items-center justify-center gap-2"><MessageCircle className="w-5 h-5" /> WhatsApp'tan Bilgi Al</a>
                <a href={agentTel} data-testid="detail-call-btn" className="w-full px-6 py-4 rounded-full btn-primary font-semibold flex items-center justify-center gap-2"><Phone className="w-5 h-5" /> Hemen Ara: {agentPhone}</a>
                {p.external_link && (
                  <a
                    href={p.external_link}
                    target="_blank"
                    rel="noopener"
                    data-testid="detail-external-link"
                    className="w-full px-6 py-4 rounded-full border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white transition-colors font-semibold flex items-center justify-center gap-2"
                  >
                    <ExternalLink className="w-5 h-5" /> İlanı Kaynakta Görüntüle
                  </a>
                )}
              </div>
              {(p.agent_name || p.agent_phone) && (
                <div className="mt-4 p-4 rounded-xl bg-slate-50 border border-slate-100" data-testid="detail-agent-card">
                  <div className="text-[10px] tracking-widest uppercase text-slate-500 font-semibold mb-1">İlan Danışmanı</div>
                  <div className="text-sm font-semibold text-slate-900">{p.agent_name || "SANTRAL EMLAK LTD"}</div>
                  {p.agent_phone && <div className="text-xs text-slate-600 mt-0.5">{p.agent_phone}</div>}
                </div>
              )}

              <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4">
                Yetki Belgesi No: <span className="font-semibold text-slate-800">{COMPANY.license}</span>
              </div>
            </div>
          </div>
        </div>

        {/* FACTS */}
        <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 bg-white p-8 rounded-2xl border border-slate-200">
          <Fact icon={Bed} label="Oda Sayısı" value={p.room_count} />
          <Fact icon={Bath} label="Banyo" value={p.bathroom_count} />
          <Fact icon={Maximize} label="Net m²" value={p.net_area ? `${p.net_area} m²` : "—"} />
          <Fact icon={Maximize} label="Brüt m²" value={p.gross_area ? `${p.gross_area} m²` : "—"} />
          <Fact icon={Layers} label="Bulunduğu Kat" value={p.floor} />
          <Fact icon={Building} label="Toplam Kat" value={p.total_floors} />
          <Fact icon={Building} label="Bina Yaşı" value={p.building_age} />
          <Fact icon={Flame} label="Isıtma" value={p.heating_type} />
          <Fact icon={Car} label="Otopark" value={p.parking ? "Var" : "Yok"} />
          <Fact icon={Building} label="Asansör" value={p.elevator ? "Var" : "Yok"} />
          <Fact icon={Trees} label="Balkon" value={p.balcony ? "Var" : "Yok"} />
          <Fact icon={Check} label="Eşyalı" value={p.furnished ? "Evet" : "Hayır"} />
        </div>

        {/* DESCRIPTION */}
        {p.description && (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-10">
            <div className="lg:col-span-2">
              <div className="label-caps mb-3">Açıklama</div>
              <h2 className="font-display text-2xl text-slate-900 mb-4">İlan Detayları</h2>
              <p className="text-slate-700 leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
            {p.features?.length > 0 && (
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="label-caps mb-4">Özellikler</div>
                <ul className="space-y-2">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-800"><Check className="w-4 h-4 text-[#C0562F]" /> {f}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* VIDEO */}
        {p.video_url && (
          <div className="mt-12">
            <div className="label-caps mb-3">Video Turu</div>
            <h2 className="font-display text-2xl text-slate-900 mb-4">İlan Videosu</h2>
            <div className="rounded-2xl overflow-hidden border border-slate-200 bg-black max-w-4xl">
              <video
                data-testid="detail-video"
                src={p.video_url.startsWith("http") ? p.video_url : `${process.env.REACT_APP_BACKEND_URL}${p.video_url}`}
                controls
                playsInline
                preload="metadata"
                className="w-full aspect-video"
              />
            </div>
          </div>
        )}

        {/* MAP */}
        <div className="mt-12">
          <div className="label-caps mb-3">Konum</div>
          <h2 className="font-display text-2xl text-slate-900 mb-4">Harita Üzerinde</h2>
          <div className="rounded-2xl overflow-hidden border border-slate-200 aspect-[16/7]">
            <iframe
              title="Konum"
              src={`https://www.google.com/maps?q=${encodeURIComponent(`${p.neighborhood || ""} ${p.district} ${p.city}`)}&output=embed`}
              className="w-full h-full"
              loading="lazy"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
